import { FC, ReactNode, createContext, useEffect, useMemo } from 'react';

import { useApp } from '@hooks/AppHooks';
import { useAdvancedQuery } from '@hooks/QueryHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';

import { getSparksByAccount } from '@helpers/sparks';
import { noop } from '@helpers/util';

import { ISparksContext } from '@app/types/SparksTypes';

export const SparksContext = createContext<ISparksContext>({
    refetchSparks: noop,
    sparks: [],
    sparksAreReady: false,
});
export const getSparksQueryKey = (accountId: string) => ['account', accountId, 'sparks'];
export const SparksProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { account, accountIsReady } = useSparkplugAccount();
    const { user, isAdminApp } = useApp();

    const {
        isLoading,
        data: sparks = [],
        refetch,
    } = useAdvancedQuery(
        getSparksQueryKey(account?._id ?? ''),
        () => (isAdminApp ? getSparksByAccount(account) : Promise.resolve([])),
        {
            enabled: isAdminApp && accountIsReady && !!account?._id && !!user,
        },
    );

    useEffect(() => {
        refetch({
            cancelRefetch: true,
        });
    }, [account?._id, user?._id]);

    const sparksAreReady = useMemo(() => {
        return !isLoading && accountIsReady && account?._id != null && user != null;
    }, [isLoading, accountIsReady, account, user]);

    const value: ISparksContext = useMemo(
        () => ({
            refetchSparks: refetch,
            sparksAreReady,
            sparks,
        }),
        [sparksAreReady, refetch, sparks],
    );

    return <SparksContext.Provider value={value}>{children}</SparksContext.Provider>;
};
